import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TroubleshootingGuide = ({ searchQuery }) => {
  const [expandedGuides, setExpandedGuides] = useState({});

  const troubleshootingGuides = [
    {
      id: 'calendar-not-syncing',
      title: 'Google Calendar Not Syncing',
      icon: 'calendar-x',
      color: '#EF4444',
      steps: [
        {
          step: 1,
          title: 'Check Connection Status',
          description: 'Go to Settings > Google Calendar Settings and verify the connection status shows "Connected".'
        },
        {
          step: 2,
          title: 'Re-authorize Access',
          description: 'Click "Disconnect" then "Connect Google Calendar" again to refresh the authorization token.'
        },
        {
          step: 3,
          title: 'Check Permissions',
          description: 'Ensure you granted calendar read/write permissions during the authorization process.'
        },
        {
          step: 4,
          title: 'Force Sync',
          description: 'Use the "Sync Now" button in Google Calendar Settings to manually trigger synchronization.'
        }
      ]
    },
    {
      id: 'notifications-not-working',
      title: 'Notifications Not Appearing',
      icon: 'bell-off',
      color: '#F59E0B',
      steps: [
        {
          step: 1,
          title: 'Check Global Settings',
          description: 'Go to Settings > Notification Settings and ensure "Enable All Notifications" is turned on.'
        },
        {
          step: 2,
          title: 'Verify Browser Permissions',
          description: 'Check your browser settings to ensure notifications are allowed for this website.'
        },
        {
          step: 3,
          title: 'Check Individual Preferences',
          description: 'Go to Profile > Personal Notifications and verify your notification preferences are enabled.'
        },
        {
          step: 4,
          title: 'Test Notifications',
          description: 'Use the "Test Notification" button in Notification Settings to verify functionality.'
        }
      ]
    },
    {
      id: 'tasks-not-saving',
      title: 'Tasks Not Saving or Disappearing',
      icon: 'alert-triangle',
      color: '#EF4444',
      steps: [
        {
          step: 1,
          title: 'Check Browser Storage',
          description: 'Ensure your browser allows local storage and hasn\'t cleared data recently.'
        },
        {
          step: 2,
          title: 'Verify All Required Fields',
          description: 'Make sure you\'ve filled in all required fields (title, date, assigned member) before saving.'
        },
        {
          step: 3,
          title: 'Create Backup',
          description: 'Go to Settings > Backup & Restore and create a backup of your current data.'
        },
        {
          step: 4,
          title: 'Clear Cache and Reload',
          description: 'Clear your browser cache and reload the application. Restore from backup if needed.'
        }
      ]
    },
    {
      id: 'parental-lock-forgotten',
      title: 'Forgot Parental Lock PIN',
      icon: 'lock',
      color: '#8B5CF6',
      steps: [
        {
          step: 1,
          title: 'Try Common PINs',
          description: 'Attempt commonly used PINs like 0000, 1234, or family birthdays.'
        },
        {
          step: 2,
          title: 'Check Backup File',
          description: 'If you have a recent backup, the PIN may be stored in the backup metadata.'
        },
        {
          step: 3,
          title: 'Reset via Browser Storage',
          description: 'Open browser developer tools (F12), go to Application > Local Storage, and remove the "parentalLockPIN" key.'
        },
        {
          step: 4,
          title: 'Last Resort: Clear All Data',
          description: 'Create a backup first, then clear all browser data for this site. You\'ll need to restore from backup and set a new PIN.'
        }
      ]
    },
    {
      id: 'slow-performance',
      title: 'App Running Slowly',
      icon: 'gauge',
      color: '#F59E0B',
      steps: [
        {
          step: 1,
          title: 'Check Browser Resources',
          description: 'Close unnecessary browser tabs and applications to free up system memory.'
        },
        {
          step: 2,
          title: 'Clear Old Data',
          description: 'Archive or delete old tasks and events that are no longer needed.'
        },
        {
          step: 3,
          title: 'Disable Animations',
          description: 'Go to Profile > Display Preferences and disable animations for better performance.'
        },
        {
          step: 4,
          title: 'Update Browser',
          description: 'Ensure you\'re using the latest version of your web browser for optimal performance.'
        }
      ]
    },
    {
      id: 'data-not-showing',
      title: 'Family Data Not Displaying',
      icon: 'eye-off',
      color: '#EF4444',
      steps: [
        {
          step: 1,
          title: 'Refresh the Page',
          description: 'Try a hard refresh (Ctrl+Shift+R or Cmd+Shift+R) to reload all data.'
        },
        {
          step: 2,
          title: 'Check Filter Settings',
          description: 'Verify that filters aren\'t hiding your data (check member filters, date ranges, categories).'
        },
        {
          step: 3,
          title: 'Verify Data Exists',
          description: 'Go to Settings > Backup & Restore and create a backup to verify data is actually stored.'
        },
        {
          step: 4,
          title: 'Restore from Backup',
          description: 'If data is missing, restore from your most recent backup file.'
        }
      ]
    }
  ];

  const filteredGuides = useMemo(() => {
    if (!searchQuery?.trim()) return troubleshootingGuides;

    const query = searchQuery?.toLowerCase();
    return troubleshootingGuides?.filter(
      guide => guide?.title?.toLowerCase()?.includes(query) ||
               guide?.steps?.some(step => 
                 step?.title?.toLowerCase()?.includes(query) ||
                 step?.description?.toLowerCase()?.includes(query)
               )
    );
  }, [searchQuery]);

  const toggleGuide = (guideId) => {
    setExpandedGuides(prev => ({
      ...prev,
      [guideId]: !prev?.[guideId]
    }));
  };

  return (
    <div className="space-y-6">
      {filteredGuides?.length > 0 ? (
        filteredGuides?.map((guide) => (
          <div key={guide?.id} className="bg-card rounded-lg shadow-warm-md overflow-hidden">
            {/* Guide Header */}
            <button
              onClick={() => toggleGuide(guide?.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-primary/5 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${guide?.color}20` }}
                >
                  <Icon name={guide?.icon} size={20} style={{ color: guide?.color }} />
                </div>
                <div>
                  <h3 className="text-h4 font-heading text-foreground">{guide?.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {guide?.steps?.length} step solution
                  </p>
                </div>
              </div>
              <Icon
                name={expandedGuides?.[guide?.id] ? 'chevron-up' : 'chevron-down'}
                size={20}
                className="text-muted-foreground"
              />
            </button>

            {/* Guide Steps */}
            {expandedGuides?.[guide?.id] && (
              <div className="p-6 pt-2 bg-muted/30">
                <div className="space-y-4">
                  {guide?.steps?.map((step) => (
                    <div key={step?.step} className="flex gap-4">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-white"
                        style={{ backgroundColor: guide?.color }}
                      >
                        {step?.step}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground mb-1">{step?.title}</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {step?.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-3">
                    Still having issues? Contact our support team for personalized assistance.
                  </p>
                  <Button variant="outline" size="sm" iconName="mail">
                    Contact Support
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))
      ) : (
        <div className="bg-card rounded-lg shadow-warm-sm p-12 text-center">
          <Icon name="search-x" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-h4 font-heading text-foreground mb-2">No troubleshooting guides found</h3>
          <p className="text-muted-foreground">
            We couldn't find any guides matching "{searchQuery}"
          </p>
        </div>
      )}
    </div>
  );
};

export default TroubleshootingGuide;